'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Submit pending role application if one was saved during signup
      if (_event === 'SIGNED_IN' && session?.user) {
        try {
          const pending = localStorage.getItem('pendingRoleApplication');
          if (pending) {
            const appData = JSON.parse(pending);
            localStorage.removeItem('pendingRoleApplication');
            // Check no existing pending application for this role
            const { data: existing } = await supabase
              .from('role_applications')
              .select('id')
              .eq('user_id', session.user.id)
              .eq('role', appData.role)
              .eq('status', 'pending')
              .maybeSingle();
            if (!existing) {
              await supabase.from('role_applications').insert({
                user_id: session.user.id,
                full_name: appData.full_name,
                email: appData.email,
                role: appData.role,
                motivation: appData.motivation,
                experience: appData.experience || null,
              });
            }
          }
        } catch {
          // Non-critical — don't break auth flow
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          avatar_url: metadata?.avatarUrl || '',
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;

    // Send branded verification email via Resend Edge Function
    if (data?.user && !data.user.email_confirmed_at) {
      try {
        await supabase.functions.invoke('send-verification-email', {
          body: { email, type: 'signup' },
        });
      } catch {
        // Non-blocking: Supabase default email is still a fallback
      }
    }

    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const getCurrentUser = async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => !!user?.email_confirmed_at;

  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
